import { useEffect, useRef } from 'react';
import type { WsEvent } from './useWebSocket';

const PUSH_EVENT_TYPES = new Set([
  'notificacion:nueva',
  'ticket:created',
]);

/**
 * Requests browser notification permission and shows push notifications
 * for critical WebSocket events.
 */
export function useNotificacionesPush(lastEvent: WsEvent | null) {
  const permissionRef = useRef<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );

  // Request permission on first render (only if not yet decided)
  useEffect(() => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') {
      Notification.requestPermission()
        .then((p) => {
          permissionRef.current = p;
        })
        .catch((err) => {
          console.warn('[useNotificacionesPush] Could not request permission:', err);
          permissionRef.current = 'denied';
        });
    }
  }, []);

  // Show notification on relevant events
  useEffect(() => {
    if (!lastEvent) return;
    if (typeof Notification === 'undefined') return;
    if (permissionRef.current !== 'granted') return;
    if (!PUSH_EVENT_TYPES.has(lastEvent.type)) return;

    // Don't notify if tab is focused
    if (document.hasFocus()) return;

    let title = 'QOps';
    let body = '';

    switch (lastEvent.type) {
      case 'notificacion:nueva':
        title = (lastEvent.data?.titulo as string) || 'Nueva notificacion';
        body = 'Tienes una nueva notificacion en QOps';
        break;
      case 'ticket:created':
        title = 'Nuevo ticket';
        body = `Ticket creado: ${lastEvent.data?.id ?? ''}`;
        break;
    }

    try {
      new Notification(title, {
        body,
        icon: '/favicon.ico',
        tag: `qops-${lastEvent.type}-${lastEvent.timestamp}`,
      });
    } catch (err) {
      console.warn('[useNotificacionesPush] Failed to show notification:', err);
    }
  }, [lastEvent]);
}
